import { Badge, Flex, Segmented, Text } from "@/atoms"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { LoadErrorEmptyTable } from "@/molecules/EmptyTable"
import { useApplicationToolInvocations } from "@/organisms/Drawers/applications/inventory/useInventory"
import { useCounts } from "@/organisms/Drawers/hooks/useCounts"
import { useParamsFilter } from "@/organisms/Drawers/hooks/useParamsFilter"
import { TabTemplate } from "@/templates/TabTemplate/TabTemplate"
import { getColumnsTable } from "./getColumns"

import styles from "./RuntimeActionsTabApplicationDrawer.module.scss"

import type { ToolDecision } from "@/api/useInventory.types"
import type { TabComponentProps } from "@/organisms/Drawers/components/TabsDrawerTemplate"

export const RUNTIME_ACTIONS_TAB = "runtimeActions"

const ALL = "all"

const DECISIONS: ToolDecision[] = ["allow", "block", "observe"]

// The decision filter is kept in the drawer's URL (shared links reopen it); an
// unknown value from the URL is no filter
const getDecision = (value: unknown) =>
  DECISIONS.find((decision) => decision === value)

// The tool calls an agent application made at runtime, as the sensor's audit hook decided them
export const RuntimeActionsTabApplicationDrawer = (
  props: TabComponentProps
) => {
  const { t } = useTranslation()
  const { t: tTab } = useTranslation("translation", {
    keyPrefix: "inventory.application.drawer.runtimeActions"
  })
  const { params, updateParams } = useParamsFilter({
    filterKey: RUNTIME_ACTIONS_TAB,
    ...props
  })
  const decision = getDecision(params.decision)
  const context = useApplicationToolInvocations(props.record?.id, decision)
  const { response, isLoading, isError, refetch } = context
  const { data, metadata } = response || {}
  // Blocked calls; blocked rows (tool and target) from an API without call counts
  const blockedCalls = metadata?.blockedInvocations
  const blocked = blockedCalls ?? metadata?.blocked ?? 0

  useCounts(RUNTIME_ACTIONS_TAB, context, { actions: props.actions })
  useTooltip(data)

  return (
    <Flex vertical gap={16} className={styles.container}>
      <Flex align="center" justify="space-between" gap={16}>
        <Segmented
          value={decision ?? ALL}
          options={[
            { label: tTab("decisions.all"), value: ALL },
            { label: tTab("decisions.allow"), value: "allow" },
            {
              label: (
                <Flex align="center" gap={6}>
                  {tTab("decisions.block")}
                  <Badge
                    count={blocked}
                    title={tTab(
                      blockedCalls === undefined
                        ? "blockedRows"
                        : "blockedCalls",
                      { count: blocked }
                    )}
                    showZero
                    color="red"
                    size="small"
                  />
                </Flex>
              ),
              value: "block"
            },
            { label: tTab("decisions.observe"), value: "observe" }
          ]}
          onChange={(value) =>
            updateParams(value === ALL ? {} : { decision: value })
          }
        />
        <Flex vertical align="end">
          {metadata?.invocations !== undefined && (
            <Text type="secondary">
              {tTab("invocations", { count: metadata.invocations })}
            </Text>
          )}
          {!!metadata?.droppedEntries && (
            <Text type="secondary">
              {tTab("droppedEntries", { count: metadata.droppedEntries })}
            </Text>
          )}
        </Flex>
      </Flex>
      <TabTemplate
        sticky
        hideFooter
        loading={isLoading}
        columns={getColumnsTable(t)}
        dataSource={data}
        locale={{
          emptyText: isError ? (
            <LoadErrorEmptyTable onRetry={refetch} />
          ) : (
            tTab("empty")
          )
        }}
      />
    </Flex>
  )
}
